import { Box, Button, Tooltip } from '@mui/material'
import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import axios from 'axios'
import CircularProgress from '@mui/material/CircularProgress'
import * as XLSX from 'xlsx-js-style'
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService'

function CombinedSalary() {
    const [earningDedComps, setEarningDedComps] = useState([])
    const [tableData, setTableData] = useState([])
    const [columns, setColumns] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const tableRef = useRef(null)
    
    const location = useLocation()
    const empDetails = location.state?.empDetails || []
    const month = location.state?.month || ''
    const year = location.state?.year || ''
    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails()
    const { companyName, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {})

    const formatNumber = (num) => {
        const roundedNum = Math.round(Number(parseFloat(num) || 0))
        return roundedNum.toLocaleString('en-US')
    }

    const getRawValue = (num) => {
        return Number(parseFloat(num) || 0)
    }

    const normalizeComponentName = (name) => {
        if (!name) return ''
        return String(name).trim().toLowerCase().replace(/\s+/g, ' ')
    }

    const getSalaryComponentRawAmount = (payroll, compName) => {
        if (!payroll) return 0
        const target = normalizeComponentName(compName)

        const ctcTotal = Array.isArray(payroll.empCTCComponents)
            ? payroll.empCTCComponents.reduce((acc, comp) => {
                if (normalizeComponentName(comp.component_name) === target) {
                    return acc + getRawValue(comp.component_amount)
                }
                return acc
            }, 0)
            : 0

        return ctcTotal
    }

    const getAutoEarningDetails = (payroll) => {
        if (!payroll) return []
        if (Array.isArray(payroll.autoEarningDetails)) return payroll.autoEarningDetails
        if (Array.isArray(payroll.auto_earning_details)) return payroll.auto_earning_details
        if (Array.isArray(payroll.empPayroll?.autoEarningDetails)) return payroll.empPayroll.autoEarningDetails
        if (Array.isArray(payroll.empPayroll?.auto_earning_details)) return payroll.empPayroll.auto_earning_details
        if (Array.isArray(payroll.empPayrollDetail?.autoEarningDetails)) return payroll.empPayrollDetail.autoEarningDetails
        if (Array.isArray(payroll.empPayrollDetail?.auto_earning_details)) return payroll.empPayrollDetail.auto_earning_details
        return []
    }

    const getAutoEarningRawAmount = (payroll, edType) => {
        const target = normalizeComponentName(edType)
        return getAutoEarningDetails(payroll).reduce((acc, item) => {
            if (normalizeComponentName(item?.ed_type) === target) {
                return acc + getRawValue(item?.amount)
            }
            return acc
        }, 0)
    }

    // Generate and download Excel file using xlsx-js-style
    const handleGenerateExcel = () => {
        if (!columns.allColumns || tableData.length === 0) return;

        const wb = XLSX.utils.book_new();
        const wsData = [];
        const numCols = columns.allColumns.length;

        // Company header rows from company details table
        const companyRow1 = [companyName || ''];
        for (let i = 1; i < numCols; i++) companyRow1.push('');
        wsData.push(companyRow1);

        const companyRow2 = [addressLine1 || ''];
        for (let i = 1; i < numCols; i++) companyRow2.push('');
        wsData.push(companyRow2);

        const companyRow3 = [addressLine2 || ''];
        for (let i = 1; i < numCols; i++) companyRow3.push('');
        wsData.push(companyRow3);

        const titleRow = [`Combined Salary details for ${month} ${year}`];
        for (let i = 1; i < numCols; i++) titleRow.push('');
        wsData.push(titleRow);

        // Add column header row
        const headerRow = columns.allColumnNames || columns.allColumns.map(c => c.name);
        wsData.push(headerRow);

        // Add data rows
        tableData.forEach((row) => {
            const dataRow = [];
            columns.allColumns.forEach((col) => {
                let value = row[col.key];
                // Handle empty/null/undefined values - show as empty string
                // But show 0 as 0
                if (value === null || value === undefined) {
                    value = '';
                } else if (typeof value === 'string') {
                    // Write numeric-looking values as numbers so Excel formatting applies.
                    const sanitized = value.replace(/,/g, '').trim();
                    if (sanitized !== '' && !Number.isNaN(Number(sanitized))) {
                        value = Number(sanitized);
                    } else {
                        value = sanitized;
                    }
                }
                dataRow.push(value);
            });
            wsData.push(dataRow);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Calculate column widths dynamically
        const calculateColumnWidths = (data) => {
            const colWidths = [];
            const numCols = data[0]?.length || 0;

            for (let col = 0; col < numCols; col++) {
                let maxWidth = 0;

                data.forEach((row, rowIndex) => {
                    // Skip header rows (rows 0-4)
                    if (rowIndex < 4) return;
                    
                    if (row[col] !== undefined && row[col] !== null) {
                        const cellValue = String(row[col]);
                        maxWidth = Math.max(maxWidth, cellValue.length);
                    }
                });

                // Add small padding (1 character) for better readability
                colWidths.push({ wch: Math.max(maxWidth + 1, 10) });
            }

            return colWidths;
        };

        ws['!cols'] = calculateColumnWidths(wsData);

        // Merge cells for company header rows
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }, // Company name
            { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } }, // Address line 1
            { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } }, // Address line 2
            { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } }  // Title row
        ];

        // Common border style
        const borderStyle = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
        };

        // Style for company name header
        const companyNameStyle = {
            font: { bold: true, sz: 16, color: { rgb: "000000" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };

        // Style for column headers (lighter blue)
        const headerStyle = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "5B9BD5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };

        // Style for data cells
        const dataStyle = {
            border: borderStyle,
            alignment: { vertical: "center" }
        };

        // Apply style to company header rows (rows 1-4)
        // Only style the first cell (A1-A4) since they're merged across all columns
        for (let row = 0; row <= 3; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            if (ws[cellAddress]) {
                ws[cellAddress].s = companyNameStyle;
            }
            
            // Remove borders from empty cells in merged range (to prevent visible lines)
            for (let col = 1; col < numCols; col++) {
                const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[emptyCellAddress]) {
                    delete ws[emptyCellAddress];
                }
            }
        }

        // Apply styles to column header row (row 5)
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let col = 0; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 4, c: col });
            if (ws[cellAddress]) {
                ws[cellAddress].s = headerStyle;
            }
        }

        // Apply styles to data cells (starting from row 6)
        for (let row = 5; row <= range.e.r; row++) {
            for (let col = 0; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    const isGrandTotal = row === range.e.r;
                    const colKey = columns.allColumns[col]?.key;
                    const align = getAlign(colKey);
                    
                    ws[cellAddress].s = {
                        ...dataStyle,
                        font: { bold: isGrandTotal || colKey === 'netPay' },
                        alignment: {
                            ...dataStyle.alignment,
                            horizontal: align
                        }
                    };

                    // Format numbers with Indian comma style
                    if (align === 'right' && colKey !== 'totalDays' && colKey !== 'workingDays' && colKey !== 'leavesTaken') {
                        ws[cellAddress].z = '#,##0';
                    }
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Combined Salary');
        XLSX.writeFile(wb, `${getShortCompanyName(companyName) || 'Report'} Report for Salary ${month.substring(0, 3)} ${year}.xlsx`);
    };

    useEffect(() => {
        const fetchEarningDedComps = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/earning-deduction-comps`)
                setEarningDedComps(response.data)
            } catch (error) {
                console.error("Error fetching earning and deduction components:", error)
                setIsLoading(false)
            }
        }
        fetchEarningDedComps()
    }, [])

    useEffect(() => {
        if (empDetails.length === 0) {
            setIsLoading(false)
            return
        }

        // Allow processing even if earningDedComps is empty (will just have empty arrays)
        // Don't return early - process with empty earning/deduction components
        console.log('Processing with earningDedComps:', earningDedComps.length);

        const earningComps = earningDedComps.filter((item) => item.deductionType === 0)
        const deductionComps = earningDedComps.filter((item) => item.earningType === 0)

        const hasRecoveryNotice = empDetails.some(
            (p) => p.notice_period_type === 'RECOVERY' || p.empPayrollDetail?.notice_period_type === 'RECOVERY'
        )
        const hasPayoutNotice = empDetails.some(
            (p) => p.notice_period_type === 'EARNING' || p.empPayrollDetail?.notice_period_type === 'EARNING'
        )

        const getRawValueForColumn = (payroll, columnKey) => {
            if (columnKey.startsWith('salComp_')) {
                const compName = columnKey.replace('salComp_', '')
                return getSalaryComponentRawAmount(payroll, compName)
            }
            if (columnKey.startsWith('earningComp_')) {
                const compId = parseInt(columnKey.replace('earningComp_', ''))
                const matched = payroll.empEarningComponents?.filter(item => item.earning_type_id === compId) || []
                return matched.reduce((acc, curr) => acc + getRawValue(curr.amount), 0)
            }
            if (columnKey.startsWith('deductionComp_')) {
                const compId = parseInt(columnKey.replace('deductionComp_', ''))
                const matched = payroll.empDeductionComponents?.filter(item => item.deduction_type_id === compId) || []
                return matched.reduce((acc, curr) => acc + getRawValue(curr.amount), 0)
            }
            if (columnKey.startsWith('autoEarning_')) {
                const edType = columnKey.replace('autoEarning_', '')
                return getAutoEarningRawAmount(payroll, edType)
            }
            const d = payroll.empPayrollDetail
            if (columnKey === 'workingDays') return d?.days_worked != null ? getRawValue(d.days_worked) : null
            if (columnKey === 'totalDays') return d?.total_days != null ? getRawValue(d.total_days) : null
            if (columnKey === 'leavesTaken') return d?.leavesTaken != null ? getRawValue(d.leavesTaken) : null
            if (columnKey === 'basic') return d?.Basic != null ? getRawValue(d.Basic) : null
            if (columnKey === 'totalPay') return d?.total_pay != null ? getRawValue(d.total_pay) : null
            if (columnKey === 'professionTax') return d?.profession_tax != null ? getRawValue(d.profession_tax) : null
            if (columnKey === 'providentFund') return d?.provident_fund != null ? getRawValue(d.provident_fund) : null
            if (columnKey === 'vpf') return d?.VPFAmount != null ? getRawValue(d.VPFAmount) : null
            if (columnKey === 'welfareFund') return d?.welfare_fund != null ? getRawValue(d.welfare_fund) : null
            if (columnKey === 'esic') return d?.Esic != null ? getRawValue(d.Esic) : null
            if (columnKey === 'tds') return (d?.income_tax != null || d?.TDS != null) ? getRawValue(d.income_tax || 0) + getRawValue(d.TDS || 0) : null
            if (columnKey === 'remainingNoticeDays') return d?.remaining_notice_days != null ? getRawValue(d.remaining_notice_days) : null
            if (columnKey === 'noticePeriodRecovery') return d?.notice_period_recovery_amount != null ? getRawValue(d.notice_period_recovery_amount) : null
            if (columnKey === 'noticePeriodShortfallDays') return d?.notice_period_shortfall_days != null ? getRawValue(d.notice_period_shortfall_days) : null
            if (columnKey === 'noticePeriodPay') return d?.notice_period_pay != null ? getRawValue(d.notice_period_pay) : null
            if (columnKey === 'totalDeductions') return d?.total_deductions != null ? getRawValue(d.total_deductions) : null
            if (columnKey === 'netPay') return d?.net_pay != null ? getRawValue(d.net_pay) : null
            return null
        }

        const hasAllZeroOrNull = (columnKey) => {
            return empDetails.every(payroll => {
                const value = getRawValueForColumn(payroll, columnKey)
                if (value == null || value === '') return true
                return Math.round(Number(value)) === 0
            })
        }

        const hasAllZeroValues = (columnKey) => {
            const keyForLegacy = columnKey.replace('SalComp_', 'salComp_').replace('EarningComp_', 'earningComp_').replace('DeductionComp_', 'deductionComp_')
            return hasAllZeroOrNull(keyForLegacy)
        }

        const filteredSalComps = empDetails[0]?.distinctSalComps?.filter(salItem =>
            !hasAllZeroValues(`SalComp_${salItem.component}`)
        ) || []

        const filteredEarningComps = earningComps.filter(item =>
            !hasAllZeroValues(`EarningComp_${item.deductionEarningId}`)
        )

        const filteredDeductionComps = deductionComps.filter(item =>
            !hasAllZeroValues(`DeductionComp_${item.deductionEarningId}`)
        )

        const uniqueAutoEarningTypes = Array.from(
            new Set(
                empDetails.flatMap(payroll =>
                    getAutoEarningDetails(payroll).map(item => item?.ed_type).filter(Boolean)
                )
            )
        )

        const filteredAutoEarningTypes = uniqueAutoEarningTypes.filter(edType =>
            !hasAllZeroOrNull(`autoEarning_${edType}`)
        )

        const showLeavesColumn = !hasAllZeroOrNull('leavesTaken')
        const showWFColumn = !hasAllZeroOrNull('welfareFund')

        // Column widths for layout (UI only; Excel uses its own widths)
        const columnWidths = {
            srNo: '60px',
            employeeCode: '120px',
            employeeName: '180px',
            workingDays: '100px',
            totalDays: '100px',
            leavesTaken: '80px',
            basic: '100px',
            welfareFund: '100px',
        }

        // Add widths for ALL components - FIXED ORDER
        const allColumns = []
        
        // Fixed columns
        allColumns.push({ key: 'srNo', name: 'Sr.no', width: '60px' })
        allColumns.push({ key: 'employeeCode', name: 'Employee Code', width: '120px' })
        allColumns.push({ key: 'employeeName', name: 'Employee Name', width: '180px' })
        
        // Days columns (hide if all 0 or null)
        if (!hasAllZeroOrNull('workingDays')) allColumns.push({ key: 'workingDays', name: 'No of days working', width: '100px' })
        if (!hasAllZeroOrNull('totalDays')) allColumns.push({ key: 'totalDays', name: 'Total Days', width: '100px' })
        if (showLeavesColumn) allColumns.push({ key: 'leavesTaken', name: 'Leaves', width: '80px' })
        if (!hasAllZeroOrNull('basic')) allColumns.push({ key: 'basic', name: 'Basic', width: '100px' })
        
        // Salary components - IN EXACT ORDER
        filteredSalComps.forEach(salItem => {
            allColumns.push({ 
                key: `salComp_${salItem.component}`, 
                name: salItem.component, 
                width: '130px' 
            })
        })
        
        // Earning components
        filteredEarningComps.forEach(item => {
            allColumns.push({ 
                key: `earningComp_${item.deductionEarningId}`, 
                name: item.earningType, 
                width: '130px' 
            })
        })

        // Auto earning details from combined salary response
        filteredAutoEarningTypes.forEach(edType => {
            allColumns.push({
                key: `autoEarning_${edType}`,
                name: edType,
                width: '130px'
            })
        })
        
        if (hasRecoveryNotice && !hasAllZeroOrNull('remainingNoticeDays')) {
            allColumns.push({ key: 'remainingNoticeDays', name: 'Remaining Notice Days', width: '150px' })
        }
        if (hasRecoveryNotice && !hasAllZeroOrNull('noticePeriodRecovery')) {
            allColumns.push({ key: 'noticePeriodRecovery', name: 'Notice Period Recovery Amount', width: '180px' })
        }
        // if (hasPayoutNotice && !hasAllZeroOrNull('noticePeriodShortfallDays')) {
        //     allColumns.push({ key: 'noticePeriodShortfallDays', name: 'Notice Period Shortfall Days', width: '180px' })
        // }
        if (hasPayoutNotice && !hasAllZeroOrNull('noticePeriodPay')) {
            allColumns.push({ key: 'noticePeriodPay', name: 'Notice Period Pay', width: '180px' })
        }

        if (!hasAllZeroOrNull('totalPay')) allColumns.push({ key: 'totalPay', name: 'Total Pay', width: '130px' })
        
        if (!hasAllZeroOrNull('professionTax')) allColumns.push({ key: 'professionTax', name: 'PT', width: '100px' })
        if (!hasAllZeroOrNull('providentFund')) allColumns.push({ key: 'providentFund', name: 'PF', width: '100px' })
        if (!hasAllZeroOrNull('vpf')) allColumns.push({ key: 'vpf', name: 'VPF', width: '100px' })
        if (showWFColumn) allColumns.push({ key: 'welfareFund', name: 'WF', width: '100px' })
        if (!hasAllZeroOrNull('esic')) allColumns.push({ key: 'esic', name: 'ESI', width: '100px' })
        
        // Deduction components
        filteredDeductionComps.forEach(item => {
            allColumns.push({ 
                key: `deductionComp_${item.deductionEarningId}`, 
                name: item.deductionType, 
                width: '130px' 
            })
        })
        
        if (!hasAllZeroOrNull('tds')) allColumns.push({ key: 'tds', name: 'TDS', width: '120px' })
        if (!hasAllZeroOrNull('totalDeductions')) allColumns.push({ key: 'totalDeductions', name: 'Total Deductions', width: '130px' })
        if (!hasAllZeroOrNull('netPay')) allColumns.push({ key: 'netPay', name: 'Net Pay Rs', width: '130px' })

        // Prepare table data with ALL columns
        const data = empDetails.map((payroll, i) => {
            const rowData = {}
            
            // Fill ALL columns with data
            allColumns.forEach(col => {
                const key = col.key
                
                if (key === 'srNo') {
                    rowData[key] = i + 1
                } else if (key === 'employeeCode') {
                    rowData[key] = payroll.empPersonalDetails.employee_code
                } else if (key === 'employeeName') {
                    rowData[key] = `${payroll.empPersonalDetails.first_name || ''} ${payroll.empPersonalDetails.last_name || ''}`.trim()
                } else if (key === 'totalDays') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.total_days)
                } else if (key === 'workingDays') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.days_worked)
                } else if (key === 'leavesTaken' && showLeavesColumn) {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.leavesTaken)
                } else if (key === 'basic') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.Basic)
                } else if (key === 'remainingNoticeDays') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.remaining_notice_days)
                } else if (key === 'noticePeriodRecovery') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.notice_period_recovery_amount)
                } else if (key === 'noticePeriodShortfallDays') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.notice_period_shortfall_days)
                } else if (key === 'noticePeriodPay') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.notice_period_pay)
                } else if (key === 'totalPay') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.total_pay)
                } else if (key === 'professionTax') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.profession_tax)
                } else if (key === 'providentFund') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.provident_fund)
                } else if (key === 'vpf') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.VPFAmount)
                } else if (key === 'welfareFund') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.welfare_fund)
                } else if (key === 'esic') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.Esic)
                } else if (key === 'tds') {
                    // Combine income_tax and TDS values
                    const incomeTax = getRawValue(payroll.empPayrollDetail.income_tax || 0)
                    const tdsValue = getRawValue(payroll.empPayrollDetail.TDS || 0)
                    rowData[key] = formatNumber(incomeTax + tdsValue)
                } else if (key === 'totalDeductions') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.total_deductions)
                } else if (key === 'netPay') {
                    rowData[key] = formatNumber(payroll.empPayrollDetail.net_pay)
                } else if (key.startsWith('salComp_')) {
                    const compName = key.replace('salComp_', '')
                    const totalAmount = getSalaryComponentRawAmount(payroll, compName)
                    rowData[key] = formatNumber(totalAmount)
                } else if (key.startsWith('earningComp_')) {
                    const compId = parseInt(key.replace('earningComp_', ''))
                    const matchedComponents = payroll.empEarningComponents?.filter(
                        item => item.earning_type_id === compId
                    ) || []
                    const totalAmount = matchedComponents.reduce(
                        (acc, curr) => acc + getRawValue(curr.amount), 0
                    )
                    rowData[key] = formatNumber(totalAmount)
                } else if (key.startsWith('deductionComp_')) {
                    const compId = parseInt(key.replace('deductionComp_', ''))
                    const matchedComponents = payroll.empDeductionComponents?.filter(
                        item => item.deduction_type_id === compId
                    ) || []
                    const totalAmount = matchedComponents.reduce(
                        (acc, curr) => acc + getRawValue(curr.amount), 0
                    )
                    rowData[key] = formatNumber(totalAmount)
                } else if (key.startsWith('autoEarning_')) {
                    const edType = key.replace('autoEarning_', '')
                    rowData[key] = formatNumber(getAutoEarningRawAmount(payroll, edType))
                }
            })
            
            rowData.key = i
            return rowData
        })

        // Add grand total row
        if (empDetails.length > 0) {
            const grandTotalRow = { key: 'grand-total' }
            
            // Calculate totals for each column
            allColumns.forEach(col => {
                const key = col.key
                
                if (key === 'srNo') {
                    grandTotalRow[key] = ''
                } else if (key === 'employeeCode') {
                    grandTotalRow[key] = 'GRAND TOTAL'
                } else if (key === 'employeeName') {
                    grandTotalRow[key] = ''
                } else if (key === 'workingDays' || key === 'totalDays') {
                    grandTotalRow[key] = ''
                } else if (key === 'leavesTaken' && !showLeavesColumn) {
                    // Skip
                } else {
                    // Calculate total for numeric columns
                    let total = 0
                    empDetails.forEach(payroll => {
                        let value = 0
                        
                        if (key === 'totalDays') {
                            value = getRawValue(payroll.empPayrollDetail.total_days)
                        } else if (key === 'workingDays') {
                            value = getRawValue(payroll.empPayrollDetail.days_worked)
                        } else if (key === 'leavesTaken') {
                            value = getRawValue(payroll.empPayrollDetail.leavesTaken)
                        } else if (key === 'basic') {
                            value = getRawValue(payroll.empPayrollDetail.Basic)
                        } else if (key === 'remainingNoticeDays') {
                            value = getRawValue(payroll.empPayrollDetail.remaining_notice_days)
                        } else if (key === 'noticePeriodRecovery') {
                            value = getRawValue(payroll.empPayrollDetail.notice_period_recovery_amount)
                        } else if (key === 'noticePeriodShortfallDays') {
                            value = getRawValue(payroll.empPayrollDetail.notice_period_shortfall_days)
                        } else if (key === 'noticePeriodPay') {
                            value = getRawValue(payroll.empPayrollDetail.notice_period_pay)
                        } else if (key === 'totalPay') {
                            value = getRawValue(payroll.empPayrollDetail.total_pay)
                        } else if (key === 'professionTax') {
                            value = getRawValue(payroll.empPayrollDetail.profession_tax)
                        } else if (key === 'providentFund') {
                            value = getRawValue(payroll.empPayrollDetail.provident_fund)
                        } else if (key === 'vpf') {
                            value = getRawValue(payroll.empPayrollDetail.VPFAmount)
                        } else if (key === 'welfareFund') {
                            value = getRawValue(payroll.empPayrollDetail.welfare_fund)
                        } else if (key === 'esic') {
                            value = getRawValue(payroll.empPayrollDetail.Esic)
                        } else if (key === 'tds') {
                            // Combine income_tax and TDS values
                            const incomeTax = getRawValue(payroll.empPayrollDetail.income_tax || 0)
                            const tdsValue = getRawValue(payroll.empPayrollDetail.TDS || 0)
                            value = incomeTax + tdsValue
                        } else if (key === 'totalDeductions') {
                            value = getRawValue(payroll.empPayrollDetail.total_deductions)
                        } else if (key === 'netPay') {
                            value = getRawValue(payroll.empPayrollDetail.net_pay)
                        } else if (key.startsWith('salComp_')) {
                            const compName = key.replace('salComp_', '')
                            value = getSalaryComponentRawAmount(payroll, compName)
                        } else if (key.startsWith('earningComp_')) {
                            const compId = parseInt(key.replace('earningComp_', ''))
                            const matchedComponents = payroll.empEarningComponents?.filter(
                                item => item.earning_type_id === compId
                            ) || []
                            value = matchedComponents.reduce((acc, curr) => acc + getRawValue(curr.amount), 0)
                        } else if (key.startsWith('deductionComp_')) {
                            const compId = parseInt(key.replace('deductionComp_', ''))
                            const matchedComponents = payroll.empDeductionComponents?.filter(
                                item => item.deduction_type_id === compId
                            ) || []
                            value = matchedComponents.reduce((acc, curr) => acc + getRawValue(curr.amount), 0)
                        } else if (key.startsWith('autoEarning_')) {
                            const edType = key.replace('autoEarning_', '')
                            value = getAutoEarningRawAmount(payroll, edType)
                        }
                        
                        total += value
                    })
                    
                    grandTotalRow[key] = key === 'employeeCode' ? 'GRAND TOTAL' : formatNumber(Math.round(total))
                }
            })
            
            data.push(grandTotalRow)
        }

        // Split columns into fixed and scrollable
        const fixedColumns = allColumns.slice(0, 3) // First 3 columns are fixed
        const scrollableColumns = allColumns.slice(3) // Rest scroll
        
        const fixedColumnNames = fixedColumns.map(col => col.name)
        const fixedColumnWidths = fixedColumns.map(col => col.width)
        const fixedColumnsTotalWidth = fixedColumnWidths.reduce((sum, width) => sum + parseInt(width), 0)
        
        const scrollableColumnNames = scrollableColumns.map(col => col.name)
        const scrollableColumnKeysOrder = scrollableColumns.map(col => col.key)
        const scrollableColumnWidths = scrollableColumns.map(col => col.width)
        const scrollableColumnsTotalWidth = scrollableColumnWidths.reduce((sum, width) => sum + parseInt(width), 0)

        const totalTableWidth = fixedColumnsTotalWidth + scrollableColumnsTotalWidth

        // Store column configuration (allColumns for single-table render)
        setColumns({
            fixedColumnNames,
            scrollableColumnNames,
            scrollableColumnKeysOrder,
            fixedColumnWidths,
            scrollableColumnWidths,
            fixedColumnsTotalWidth,
            scrollableColumnsTotalWidth,
            totalTableWidth,
            showLeavesColumn,
            allColumns,
            allColumnKeys: allColumns.map(c => c.key),
            allColumnNames: allColumns.map(c => c.name),
            allColumnWidths: allColumns.map(c => c.width)
        })
        
        setTableData(data)
        setIsLoading(false)

    }, [empDetails, earningDedComps])

    const getAlign = (key) => {
        if (
            key === 'workingDays' ||
            key === 'totalDays' ||
            key === 'leavesTaken' ||
            key === 'basic' ||
            key.startsWith('salComp_') ||
            key.startsWith('earningComp_') ||
            key.startsWith('autoEarning_') ||
            key.startsWith('deductionComp_') ||
            key === 'totalPay' ||
            key === 'professionTax' ||
            key === 'providentFund' ||
            key === 'welfareFund' ||
            key === 'esic' ||
            key === 'tds' ||
            key === 'totalDeductions' ||
            key === 'netPay'
        ) return 'right'
        if (key === 'employeeName') return 'left'
        if (key === 'employeeCode') return 'left'
        return 'center'
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "10px", overflowX: "hidden", maxWidth: "100vw" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", padding: '20px', minWidth: 0, overflow: "hidden" }}>
                <Button
                    variant='contained'
                    color='success'
                    onClick={handleGenerateExcel}
                    sx={{ marginBottom: 2 }}
                >
                    Download Combined Salary Report (excel)
                </Button>

                {tableData.length === 0 || !columns.allColumns || columns.allColumns.length === 0 ? (
                    <Box sx={{ textAlign: 'center', padding: 4 }}>
                        No data available
                    </Box>
                ) : (
                    <div
                        ref={tableRef}
                        style={{
                            height: '600px',
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'auto',
                            overflowX: 'auto',
                            overflowY: 'auto',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            isolation: 'isolate'
                        }}
                    >
                        {/* Single table = header and records always aligned */}
                        <table
                            style={{
                                display: 'table',
                                overflow: 'visible',
                                whiteSpace: 'normal',
                                width: `${columns.totalTableWidth}px`,
                                minWidth: `${columns.totalTableWidth}px`,
                                tableLayout: 'fixed',
                                borderCollapse: 'collapse',
                                margin: 0
                            }}
                            className="table"
                        >
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                                <tr>
                                    {columns.allColumns.map((col, index) => {
                                        const isFixed = index < 3
                                        const left = isFixed ? (index === 0 ? 0 : columns.fixedColumnWidths.slice(0, index).reduce((s, w) => s + parseInt(w), 0)) : undefined
                                        return (
                                            <th
                                                key={col.key}
                                                style={{
                                                    width: col.width,
                                                    minWidth: col.width,
                                                    padding: '12px 8px',
                                                    boxSizing: 'border-box',
                                                    textAlign: getAlign(col.key),
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    border: 'none',
                                                    borderRight: isFixed ? (index < 2 ? '1px solid #444' : '2px solid #dee2e6') : undefined,
                                                    ...(isFixed && { position: 'sticky', left: `${left}px`, zIndex: 11, background: '#212529' })
                                                }}
                                            >
                                                <Tooltip title={col.name} placement="top" arrow>
                                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.name}</span>
                                                </Tooltip>
                                            </th>
                                        )
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map((row) => (
                                    <tr
                                        key={row.key}
                                        style={{
                                            height: '48px',
                                            backgroundColor: row.key === 'grand-total' ? '#f8f9fa' : 'white'
                                        }}
                                    >
                                        {columns.allColumns.map((col, index) => {
                                            const isFixed = index < 3
                                            const left = isFixed ? (index === 0 ? 0 : columns.fixedColumnWidths.slice(0, index).reduce((s, w) => s + parseInt(w), 0)) : undefined
                                            const value = row[col.key] ?? ''
                                            const bg = row.key === 'grand-total' ? '#f8f9fa' : 'white'
                                            return (
                                                <td
                                                    key={col.key}
                                                    style={{
                                                        width: col.width,
                                                        minWidth: col.width,
                                                        padding: '12px 8px',
                                                        boxSizing: 'border-box',
                                                        textAlign: getAlign(col.key),
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid #dee2e6',
                                                        borderRight: isFixed ? (index < 2 ? '1px solid #dee2e6' : '2px solid #dee2e6') : undefined,
                                                        fontWeight: (col.key === 'netPay' || row.key === 'grand-total') ? 'bold' : 'normal',
                                                        ...(isFixed && { position: 'sticky', left: `${left}px`, zIndex: 5, background: bg })
                                                    }}
                                                >
                                                    {value}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Box>
        </Box>
    )
}

export default CombinedSalary